import React, {
  useState,
  useEffect,
  useRef,
  RefObject,
  useCallback,
  KeyboardEvent,
} from 'react';
import {
  CellContext,
} from '@tanstack/react-table';
import defaults from 'lodash/defaults';
import isNil from 'lodash/isNil';
import {
  EditableTableMeta,
  EditableCellProps,
  VirtualTableColumnDef as ColumnDef,
  EditableRendererProps,
  ColumnDefMeta,
} from 'core/components/VirtualTable/types';
import Input from '@/components/Input';
import Editable from '@/core/components/Editable';

function DefaultCell<T>({ getValue }: CellContext<T, unknown>) {
  return getValue();
}

function defaultRenderer({
  controlRef,
  onSubmit,
  onCancel,
  onChange,
  props,
  value,
}: EditableRendererProps<string>) {
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      onSubmit?.();
    } else if (e.key === 'Escape') {
      onCancel?.();
    }
  }, [onSubmit, onCancel]);

  return (
    <Input
      {...props}
      inputRef={controlRef as RefObject<HTMLInputElement>}
      value={value}
      onChange={onChange}
      onBlur={onSubmit}
      onKeyDown={handleKeyDown}
    />
  );
}

function EditableCell<T>({ IdleView, ...context }: EditableCellProps<T>) {
  const {
    getValue,
    row: { index, original },
    column: {
      id,
      columnDef: { meta },
    },
    table,
  } = context;

  const { editable } = meta as ColumnDefMeta<T>;
  const editableObject = typeof editable === 'object' ? editable : undefined;
  const tableMeta = table.options.meta as EditableTableMeta<T>;

  const {
    validate,
    props,
    Component,
    alwaysEditable,
    mode,
  } = defaults(editableObject, {
    validate: () => true,
    props: {},
    alwaysEditable: false,
    mode: 'onSubmit',
  });

  const controlRef = useRef<HTMLElement>(null);
  const initialValue = getValue<T>();
  // We need to keep and update the state of the cell normally
  const [value, setValue] = useState<T>(initialValue);

  // When the input is blurred, we'll call our table meta's updateData function
  const handleSubmit = useCallback((_value?: T) => {
    const newValue = _value ?? value;
    const isValid = validate(newValue);
    if (isValid && initialValue !== newValue) {
      tableMeta?.updateData(index, id, newValue, original);
    }
    return isValid;
  }, [value]);

  const onRemove = () => {
    tableMeta?.removeData(index, original);
  };

  const handleCancel = () => { setValue(initialValue); };

  const handleChange = (value: T) => {
    setValue(value);
    if (mode === 'onChange' && initialValue !== value) {
      handleSubmit(value);
    }
  };

  // If the initialValue is changed external, sync it up with our state
  useEffect(() => { setValue(initialValue); }, [initialValue]);

  return (
    <Editable
      idleView={alwaysEditable ? null : <IdleView {...context} />}
      controlRef={controlRef as RefObject<HTMLInputElement>}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    >
      {(onSubmit, onCancel, inputRef) => (
        isNil(Component)
          ? defaultRenderer({
            onSubmit,
            onCancel,
            controlRef: inputRef,
            value: value as string,
            onChange: (value: string) => { handleChange(value as T); },
            props,
          })
          : (
            <Component
              onSubmit={onSubmit}
              onRemove={onRemove}
              onChange={handleChange}
              onCancel={onCancel}
              value={value}
              controlRef={inputRef}
              props={props}
              data={original}
            />
          )
      )}
    </Editable>
  );
}

export default function withEditableCell<T>({ cell, ...columnDef }: ColumnDef<T>): ColumnDef<T> {
  const IdleView = cell ?? DefaultCell<T>;
  return {
    ...columnDef,
    cell: (context) => (
      <EditableCell<T>
        {...context}
        IdleView={IdleView}
      />
    ),
  };
}
